// Shared spectrum frame renderer used by music/midi viewers and simulators.
// Keep this module pure (CanvasRenderingContext2D only) for maximum reuse.

export function drawSpectrumFrame(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  data: Uint8Array | null,
  nyquist: number,
  maxFreqHz: number,
  color: string,
  status: string,
) {
  // Background
  ctx.fillStyle = '#000'
  ctx.fillRect(0, 0, w, h)

  const pad = { left: 48, right: 16, top: 24, bottom: 32 }
  const plotW = w - pad.left - pad.right
  const plotH = h - pad.top - pad.bottom
  const px0 = pad.left
  const py0 = pad.top

  // Grid
  ctx.strokeStyle = '#1c2128'
  ctx.lineWidth = 1
  const hLines = 6
  for (let i = 0; i <= hLines; i++) {
    const y = py0 + (plotH * i) / hLines
    ctx.beginPath(); ctx.moveTo(px0, y); ctx.lineTo(px0 + plotW, y); ctx.stroke()
  }
  const vLines = 10
  for (let i = 0; i <= vLines; i++) {
    const x = px0 + (plotW * i) / vLines
    ctx.beginPath(); ctx.moveTo(x, py0); ctx.lineTo(x, py0 + plotH); ctx.stroke()
  }

  // X-axis labels (Hz)
  ctx.fillStyle = '#484f58'
  ctx.font = '10px monospace'
  ctx.textAlign = 'center'
  for (let i = 0; i <= vLines; i++) {
    const f = (maxFreqHz * i) / vLines
    const x = px0 + (plotW * i) / vLines
    ctx.fillText(f >= 1000 ? `${(f / 1000).toFixed(1)}k` : `${Math.round(f)}`, x, py0 + plotH + 16)
  }
  ctx.fillText('Hz', px0 + plotW + 8, py0 + plotH + 16)

  // Y-axis labels (normalized)
  ctx.textAlign = 'right'
  for (let i = 0; i <= hLines; i++) {
    const val = (1 - i / hLines)
    const y = py0 + (plotH * i) / hLines
    ctx.fillText(val.toFixed(2), px0 - 6, y + 4)
  }

  // Title
  ctx.fillStyle = '#8b949e'
  ctx.font = '12px sans-serif'
  ctx.textAlign = 'left'
  ctx.fillText('Spectrum Analyzer', px0, py0 - 8)

  if (status === 'idle' || !data) return

  // How many bins map to <= maxFreqHz?
  const maxBin = Math.min(data.length, Math.floor((maxFreqHz / nyquist) * data.length))
  if (maxBin < 2) return

  // Filled area
  ctx.fillStyle = color + '30'
  ctx.strokeStyle = color
  ctx.lineWidth = 1.5
  ctx.globalAlpha = 0.85

  ctx.beginPath()
  ctx.moveTo(px0, py0 + plotH)
  for (let i = 0; i < maxBin; i++) {
    const mag = data[i] / 255.0
    const x = px0 + (plotW * i) / maxBin
    const y = py0 + plotH * (1 - mag)
    ctx.lineTo(x, y)
  }
  ctx.lineTo(px0 + (plotW * (maxBin - 1)) / maxBin, py0 + plotH)
  ctx.closePath()
  ctx.fill()

  // Outline
  ctx.beginPath()
  for (let i = 0; i < maxBin; i++) {
    const mag = data[i] / 255.0
    const x = px0 + (plotW * i) / maxBin
    const y = py0 + plotH * (1 - mag)
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y)
  }
  ctx.stroke()

  // Peak markers
  const threshold = 255 * 0.40
  const peaks: { x: number; y: number; freqHz: number }[] = []
  for (let i = 1; i < maxBin - 1; i++) {
    if (data[i] > data[i - 1] && data[i] > data[i + 1] && data[i] > threshold) {
      const freqHz = (i / data.length) * nyquist
      if (freqHz < 20) continue
      const x = px0 + (plotW * i) / maxBin
      const y = py0 + plotH * (1 - data[i] / 255.0)
      peaks.push({ x, y, freqHz })
    }
  }

  peaks.sort((a, b) => b.y - a.y).slice(0, 12).forEach((pk) => {
    ctx.strokeStyle = '#58a6ff'
    ctx.lineWidth = 0.8
    ctx.globalAlpha = 0.35
    ctx.setLineDash([3, 3])
    ctx.beginPath(); ctx.moveTo(pk.x, pk.y); ctx.lineTo(pk.x, py0 + plotH); ctx.stroke()
    ctx.setLineDash([])
    ctx.globalAlpha = 1

    ctx.fillStyle = '#58a6ff'
    ctx.beginPath()
    ctx.moveTo(pk.x, pk.y - 5)
    ctx.lineTo(pk.x + 4, pk.y)
    ctx.lineTo(pk.x, pk.y + 5)
    ctx.lineTo(pk.x - 4, pk.y)
    ctx.closePath()
    ctx.fill()

    ctx.fillStyle = '#e6edf3'
    ctx.font = 'bold 9px monospace'
    ctx.textAlign = 'center'
    const label = pk.freqHz >= 1000
      ? `${(pk.freqHz / 1000).toFixed(1)}kHz`
      : `${Math.round(pk.freqHz)}Hz`
    ctx.fillText(label, pk.x, pk.y > py0 + 20 ? pk.y - 10 : py0 + plotH - 14)
  })

  ctx.globalAlpha = 1
  ctx.textAlign = 'left'
}
